interface Kural {
  id: number;
  ad: string;
  tetikleyici: string;
  sablon: string;
  hedefKitle: string;
  durum: string;
  sonCalisma: string;
  sonrakiCalisma: string;
  aciklama: string;
  gonderilenSayisi: number;
}

interface KuralKartiProps {
  kural: Kural;
  onToggle: () => void;
  onDetay: () => void;
  onDuzenle: () => void;
}

const TETIK_ICONS: Record<string, string> = {
  "Ödeme tarihinden 1 gün önce hatırlatma": "ri-calendar-check-line",
  "Ödeme gecikirse takip mesajı": "ri-time-line",
  "Taahhüt bitişinden 30 gün önce bilgilendirme": "ri-file-warning-line",
  "Her ay başında referans isteme": "ri-repeat-line",
  "Yeni kayıt sonrası 7. gün takip mesajı": "ri-user-received-line",
  "Deneme ders sonrası takip mesajı": "ri-book-open-line",
};

const KURAL_COLORS: Record<number, string> = {
  1: "bg-amber-100 text-amber-700",
  2: "bg-red-100 text-red-600",
  3: "bg-orange-100 text-orange-700",
  4: "bg-violet-100 text-violet-700",
  5: "bg-sky-100 text-sky-700",
  6: "bg-gray-100 text-gray-500",
};

export default function KuralKarti({ kural, onToggle, onDetay, onDuzenle }: KuralKartiProps) {
  const isActive = kural.durum === "Aktif";
  const iconKey = kural.ad;

  return (
    <div className={`bg-white rounded-2xl border transition-all ${isActive ? "border-gray-100" : "border-gray-100 opacity-75"}`}>
      {/* Color top strip */}
      <div className={`h-1 rounded-t-2xl ${isActive ? "bg-[#25D366]" : "bg-gray-200"}`} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${KURAL_COLORS[kural.id] || "bg-gray-100 text-gray-500"}`}>
            <i className={`${TETIK_ICONS[iconKey] || "ri-notification-3-line"} text-base`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-900 leading-tight">{kural.ad}</h3>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold
                ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`
              }>
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-gray-400"}`}></span>
                {kural.durum}
              </span>
              <span className="text-[11px] text-gray-400">{kural.gonderilenSayisi} gönderildi</span>
            </div>
          </div>

          {/* Toggle switch */}
          <button
            onClick={onToggle}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${isActive ? "bg-[#25D366]" : "bg-gray-200"}`}
          >
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 leading-relaxed mb-4">{kural.aciklama}</p>

        {/* Details */}
        <div className="space-y-2">
          {[
            { icon: "ri-flashlight-line", label: "Tetikleyici", value: kural.tetikleyici },
            { icon: "ri-file-text-line", label: "Mesaj Şablonu", value: kural.sablon },
            { icon: "ri-group-line", label: "Hedef Kitle", value: kural.hedefKitle },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2.5 text-xs">
              <div className="w-5 h-5 flex items-center justify-center text-gray-400 flex-shrink-0">
                <i className={`${item.icon} text-xs`}></i>
              </div>
              <span className="text-gray-400 w-24 flex-shrink-0">{item.label}:</span>
              <span className="text-gray-700 font-medium truncate">{item.value}</span>
            </div>
          ))}
        </div>

        {/* Run times */}
        <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-[10px] text-gray-400 mb-0.5">Son Çalışma</p>
            <p className="text-xs font-semibold text-gray-700">{kural.sonCalisma}</p>
          </div>
          <div className={`rounded-xl p-3 ${isActive ? "bg-emerald-50" : "bg-gray-50"}`}>
            <p className="text-[10px] text-gray-400 mb-0.5">Sonraki Çalışma</p>
            <p className={`text-xs font-semibold ${isActive ? "text-emerald-700" : "text-gray-400"}`}>
              {kural.sonrakiCalisma}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
          <button
            onClick={onDetay}
            className="flex-1 py-2 text-xs font-medium border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-eye-line text-xs"></i>
            </div>
            Detay
          </button>
          <button
            onClick={onDuzenle}
            className="flex-1 py-2 text-xs font-medium border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-edit-line text-xs"></i>
            </div>
            Düzenle
          </button>
          <button
            onClick={onToggle}
            className={`flex-1 py-2 text-xs font-medium rounded-xl cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5
              ${isActive
                ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              }
            `}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={`${isActive ? "ri-pause-line" : "ri-play-line"} text-xs`}></i>
            </div>
            {isActive ? "Durdur" : "Başlat"}
          </button>
        </div>
      </div>
    </div>
  );
}